import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { AdminUser } from 'libs/end-to-end/src/admin';
import { ExtraOrganization } from 'libs/end-to-end/src/admin';
import { ExtraOrganizations } from 'libs/end-to-end/src/admin';
import { CreateAdminUser } from 'libs/end-to-end/src/admin';
import { OrganizationUserRole } from 'libs/end-to-end/src/types/users/organization-user-roles';
import { UpdateAdminUser } from 'libs/end-to-end/src/admin';
import { getPasswordComplexityErrorCode } from 'libs/end-to-end/src/passwords';
import { mapOverEnum } from 'libs/end-to-end/src/arrays';
import { AdminUserEnrollmentType } from 'libs/end-to-end/src/admin';
import React, { useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { ColumnDefinition } from 'apps/sigma2-ui/src/components/ui/Table';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Checkbox } from '@mui/material';
import { IconVisibility24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibility24px';
import { IconVisibilityOff24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconVisibilityOff24px';

const getDisplayError = (error: BaseError): string => {
  if (error.errorType === 'PasswordComplexityError') {
    return 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.';
  }

  if (error.errorType === 'EmailAlreadyTakenError') {
    return 'A user with this email already exists.';
  }

  return 'An error occurred. Please try again.';
};

export const UserPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { userId: routeUserId } = useParams();

  if (!routeUserId) {
    throw new Error('User ID is required');
  }

  const isNewUser = routeUserId === 'new';
  const userId = routeUserId === 'new' ? null : routeUserId;

  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationRole, setOrganizationRole] =
    useState<OrganizationUserRole | null>(null);
  const [extraOrganizations, setExtraOrganizations] =
    useState<ExtraOrganizations>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [password, setPassword] = useState<string>('');
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [enrollmentType, setEnrollmentType] =
    useState<AdminUserEnrollmentType | null>(null);
  const [organizations, setOrganizations] = useState<Organizations | null>(
    null,
  );
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [mostRecentError, setMostRecentError] = useState<string | null>(null);
  const language = useLanguage();

  const navigate = useNavigateSafe();

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive() || !myUser) {
        return;
      }

      if (myUser.roleAssignments !== 'superAdmin' || hasLoadedOnce) {
        return;
      }

      setHasLoadedOnce(true);

      try {
        setIsLoadingOrganizations(true);

        const getOrganizationsResult = await userAuthenticatedApiCall({
          responseSchema: Organizations,
          url: `${getApiBaseUrl()}/organizations`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (
          !isAlive() ||
          !getOrganizationsResult ||
          getOrganizationsResult.resultType === 'err'
        ) {
          return;
        }

        const organizations = getOrganizationsResult.value;
        setOrganizations(organizations);

        if (isNewUser) {
          setEmail('');
          setOrganizationId(null);
          setOrganizationRole(null);
          setIsSuperAdmin(false);
          setPassword('');
          setEnrollmentType('local');
          setExtraOrganizations([]);
          return;
        }

        setIsLoadingUser(true);

        const getUserResult = await userAuthenticatedApiCall({
          responseSchema: AdminUser,
          url: `${getApiBaseUrl()}/admin/users/${userId}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (
          !isAlive() ||
          !getUserResult ||
          getUserResult.resultType === 'err'
        ) {
          return;
        }

        const user = getUserResult.value;
        setEmail(user.email);
        setOrganizationId(user.organizationId);
        setOrganizationRole(user.organizationRole);
        setIsSuperAdmin(user.isSuperAdmin);
        setEnrollmentType(user.enrollmentType);
        setExtraOrganizations(user.extraOrganizations);
      } finally {
        if (isAlive()) {
          setIsLoadingUser(false);
          setIsLoadingOrganizations(false);
        }
      }
    },
    [myUser, hasLoadedOnce, userId, userAuthenticatedApiCall, isNewUser],
  );

  const isEmailAndPasswordEditable = useMemo(() => {
    if (isNewUser) {
      return true;
    }

    return !enrollmentType || enrollmentType === 'local';
  }, [isNewUser, enrollmentType]);

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const updateUser = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        isNewUser ||
        isLoadingUser ||
        email === null ||
        isSuperAdmin === null ||
        isUpdatingUser
      ) {
        return;
      }

      setIsUpdatingUser(true);

      try {
        const requestUser: UpdateAdminUser = {
          email,
          organizationId: isSuperAdmin
            ? null
            : organizationId !== null && organizationId.length > 0
              ? organizationId
              : null,
          organizationRole: isSuperAdmin ? null : organizationRole,
          isSuperAdmin,
          password: password.length > 0 ? password : null,
          extraOrganizations: isSuperAdmin ? [] : extraOrganizations,
        };

        const updateResult = await userAuthenticatedApiCall({
          responseSchema: AdminUser,
          url: `${getApiBaseUrl()}/admin/users/${userId}`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(requestUser),
          },
        });

        if (!isAlive() || !updateResult) {
          return;
        }

        if (updateResult.resultType === 'err') {
          setMostRecentError(getDisplayError(updateResult.error));
          return;
        }

        navigate('/users');
      } finally {
        if (isAlive()) {
          setIsUpdatingUser(false);
        }
      }
    },
    [
      email,
      isLoadingUser,
      isSuperAdmin,
      isUpdatingUser,
      navigate,
      password,
      userId,
      userAuthenticatedApiCall,
      organizationId,
      organizationRole,
      isNewUser,
      extraOrganizations,
    ],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const createUser = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        !isNewUser ||
        !hasLoadedOnce ||
        isLoadingOrganizations ||
        email === null ||
        password === null ||
        isSuperAdmin === null
      ) {
        return;
      }

      try {
        setIsCreatingUser(true);

        const requestUser: CreateAdminUser = {
          email,
          organizationId: isSuperAdmin
            ? null
            : organizationId !== null && organizationId.length > 0
              ? organizationId
              : null,
          organizationRole: isSuperAdmin ? null : organizationRole,
          isSuperAdmin,
          password,
          extraOrganizations: isSuperAdmin ? [] : extraOrganizations,
        };

        const createResult = await userAuthenticatedApiCall({
          responseSchema: AdminUser,
          url: `${getApiBaseUrl()}/admin/users`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(requestUser),
          },
        });

        if (!isAlive() || !createResult) {
          return;
        }

        if (createResult.resultType === 'err') {
          setMostRecentError(getDisplayError(createResult.error));
          return;
        }

        navigate('/users');
      } finally {
        setIsCreatingUser(false);
      }
    },
    [
      hasLoadedOnce,
      isLoadingOrganizations,
      isNewUser,
      email,
      password,
      isSuperAdmin,
      navigate,
      organizationId,
      organizationRole,
      userAuthenticatedApiCall,
      extraOrganizations,
    ],
  );

  const passwordComplexityErrorCode = useMemo(() => {
    return getPasswordComplexityErrorCode(password);
  }, [password]);
  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  // eslint-disable-next-line complexity
  const isSaveDisabled = useMemo(() => {
    if (
      isLoadingUser ||
      isLoadingOrganizations ||
      password === null ||
      email === null ||
      isSuperAdmin === null
    ) {
      return true;
    }

    const isInvalidPassword =
      password.length > 0 && passwordComplexityErrorCode !== null;

    const isEmptyPassword = password.length === 0 && isNewUser;

    if (isInvalidPassword || isEmptyPassword) {
      return true;
    }

    return (
      !isSuperAdmin && (organizationId === null || organizationRole === null)
    );
  }, [
    isLoadingUser,
    isLoadingOrganizations,
    password,
    email,
    isSuperAdmin,
    passwordComplexityErrorCode,
    isNewUser,
    organizationId,
    organizationRole,
  ]);

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const children = useMemo(() => {
    if (
      isLoadingUser ||
      isLoadingOrganizations ||
      isUpdatingUser ||
      isCreatingUser
    ) {
      return <p>Loading...</p>;
    }

    if (email === null || isSuperAdmin === null || organizations === null) {
      return <p>Unable to load user data.</p>;
    }

    let organizationSettings: React.ReactNode = <></>;

    if (!isSuperAdmin) {
      const organizationOptions: React.ReactNode[] = [
        <option key={'none'} value={''}>
          Select an organization...
        </option>,
      ];

      for (const organization of organizations.organizations) {
        organizationOptions.push(
          <option key={organization.id} value={organization.id}>
            {organization.name}
          </option>,
        );
      }

      const organizationRoleOptions: React.ReactNode[] = [
        <option key={'none'} value={''}>
          Select a role...
        </option>,
        ...mapOverEnum(OrganizationUserRole, (role) => {
          return (
            <option key={role} value={role}>
              {role}
            </option>
          );
        }),
      ];

      const organizationRoleSelector =
        organizationId === null || organizationId.length === 0 ? (
          <></>
        ) : (
          <div className="flex flex-row mb-2">
            <div className="basis-1/2">
              <label className="block mb-1">
                Organization Role:{' '}
                <select
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const newRole =
                      selectedValue === ''
                        ? null
                        : OrganizationUserRole.parse(selectedValue);
                    setOrganizationRole(newRole);
                  }}
                  defaultValue={
                    organizationRole === null ? '' : organizationRole
                  }
                  className="form-select border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 w-full transition"
                >
                  {organizationRoleOptions}
                </select>
              </label>
            </div>
          </div>
        );

      organizationSettings = (
        <>
          <div className="flex flex-row mb-2">
            <div className="basis-1/2">
              <label className="block mb-1">
                Organization:{' '}
                <select
                  onChange={(e) =>
                    setOrganizationId(
                      e.target.value === '' ? null : e.target.value,
                    )
                  }
                  defaultValue={organizationId === null ? '' : organizationId}
                  className="form-select border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 w-full transition"
                >
                  {organizationOptions}
                </select>
              </label>
            </div>
          </div>
          {organizationRoleSelector}
        </>
      );
    }

    const isSelf = !!myUser && myUser.id === userId;

    const welcomeMessage = isNewUser
      ? 'Create a new user.'
      : `You are managing the user ${email} (${userId})`;

    let passwordSettings: React.ReactNode;

    if (isNewUser) {
      passwordSettings = (
        <div className="flex flex-row mb-2">
          <div className="basis-1/2">
            <label htmlFor="password" className="block mb-1">
              Password (required):
            </label>
            <div className="relative">
              <TextInput
                id="password"
                name="password"
                data-qa="passwordInput"
                type={isShowingPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <button
                data-qa="passwordVisibilityToggle"
                type="button"
                className="text-gray-900 hover:text-tertiary w-6 absolute mt-1.5 top-0 h-12 focus-visible:outline-none focus-visible:text-tertiary"
                style={{ bottom: '1rem', right: '1rem' }}
                title={isShowingPassword ? 'hide password' : 'show password'}
                onClick={() => {
                  setIsShowingPassword((showing) => !showing);
                }}
                tabIndex={-1}
              >
                {isShowingPassword ? (
                  <IconVisibilityOff24pxSvg />
                ) : (
                  <IconVisibility24pxSvg />
                )}
              </button>
            </div>
            {password.length > 0 && passwordComplexityErrorCode && (
              <S2Typography
                variant={'small-label-alt'}
                textColorVariant={'red-600'}
                translatedContent={language.passwordComplexityErrorMessage({
                  passwordComplexityErrorCode,
                })}
              />
            )}
          </div>
        </div>
      );
    } else {
      passwordSettings = (
        <div className="flex flex-row mb-2">
          <div className="basis-1/2">
            <label htmlFor="password" className="block mb-1">
              Change Password (leave blank to keep current password):
            </label>
            <div className="relative">
              <TextInput
                id="password"
                data-qa="passwordInput"
                type={isShowingPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <button
                data-qa="passwordVisibilityToggle"
                type="button"
                className="text-gray-900 hover:text-tertiary w-6 absolute mt-1.5 top-0 h-12 focus-visible:outline-none focus-visible:text-tertiary"
                style={{ bottom: '1rem', right: '1rem' }}
                title={isShowingPassword ? 'hide password' : 'show password'}
                onClick={() => {
                  setIsShowingPassword((showing) => !showing);
                }}
                tabIndex={-1}
              >
                {isShowingPassword ? (
                  <IconVisibilityOff24pxSvg />
                ) : (
                  <IconVisibility24pxSvg />
                )}
              </button>
            </div>
            {password.length > 0 && passwordComplexityErrorCode && (
              <S2Typography
                variant={'small-label-alt'}
                textColorVariant={'red-600'}
                translatedContent={language.passwordComplexityErrorMessage({
                  passwordComplexityErrorCode,
                })}
              />
            )}
          </div>
        </div>
      );
    }
    return (
      <>
        <p>{welcomeMessage}</p>

        {mostRecentError && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-2"
            role="alert"
          >
            <strong className="font-bold">Error</strong>
            <span className="block pl-2">{mostRecentError}</span>
          </div>
        )}

        <div className="flex flex-row">
          <div className="basis-1/4">
            <label className="block mb-1">
              Is Super Admin?:{' '}
              <Checkbox
                checked={isSuperAdmin}
                disabled={isSelf}
                onChange={(e) => setIsSuperAdmin(e.target.checked)}
              />
            </label>
          </div>
        </div>

        {organizationSettings}

        <div className="flex flex-row mb-2">
          <div className="basis-1/2 mr-5">
            <label className="block mb-1">
              Email:{' '}
              <TextInput
                id="email"
                type="email"
                name="email"
                data-qa="emailInput"
                value={email || ''}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={!isEmailAndPasswordEditable}
              />
            </label>
          </div>
        </div>

        {passwordSettings}

        {!isSuperAdmin && (
          <ExtraOrganizationsSection
            organizations={organizations}
            extraOrganizations={extraOrganizations}
            setExtraOrganizations={setExtraOrganizations}
          />
        )}

        <div>
          <LegacyButton
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
            onClick={isNewUser ? createUser : updateUser}
            disabled={isSaveDisabled}
          >
            Save
          </LegacyButton>
        </div>
      </>
    );
  }, [
    isLoadingUser,
    isLoadingOrganizations,
    isUpdatingUser,
    isCreatingUser,
    email,
    isSuperAdmin,
    organizations,
    myUser,
    userId,
    isNewUser,
    mostRecentError,
    isEmailAndPasswordEditable,
    extraOrganizations,
    createUser,
    updateUser,
    isSaveDisabled,
    organizationId,
    organizationRole,
    isShowingPassword,
    password,
    passwordComplexityErrorCode,
    language,
  ]);

  return (
    <AdminLayout currentPage={'User'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Users', to: '/users' },
          {
            label: isNewUser ? 'Create User' : 'Edit User',
            to: isNewUser ? '/users/new' : `/users/${userId}`,
          },
        ]}
      />
      {children}
    </AdminLayout>
  );
};

const ExtraOrganizationsSection = ({
  extraOrganizations,
  setExtraOrganizations,
  organizations,
}: {
  extraOrganizations: ExtraOrganizations;
  setExtraOrganizations: SetterWithReducer<ExtraOrganizations>;
  organizations: Organizations;
}) => {
  const [selectedExtraOrganizationId, setSelectedExtraOrganizationId] =
    useState<string>('');

  const [selectedExtraOrganizationRole, setSelectedExtraOrganizationRole] =
    useState<OrganizationUserRole | null>(null);

  const language = useLanguage();

  const columnDefinitions: ColumnDefinition<ExtraOrganization>[] =
    useMemo(() => {
      return [
        {
          id: 'name',
          header: () => (
            <S2Typography
              variant="body-6"
              translatedContent={language.organization()}
            />
          ),
          cell: ({ item }) => (
            <S2Typography
              variant="body-6"
              translatedContent={language.dangerousUserInput({
                value:
                  organizations.organizations.find(
                    (o) => o.id === item.organizationId,
                  )?.name ?? '',
              })}
            />
          ),
        },
        {
          id: 'role',
          header: () => (
            <S2Typography
              variant="body-6"
              translatedContent={language.role()}
            />
          ),
          cell: ({ item }) => (
            <S2Typography
              variant="body-6"
              translatedContent={language.dangerousUserInput({
                value: item.organizationRole,
              })}
            />
          ),
        },
        {
          id: 'deleteButton',
          header: () => (
            <S2Typography
              variant={'body-6'}
              translatedContent={language.delete()}
            />
          ),
          cell: ({ item }) => {
            return (
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    setExtraOrganizations((existingExtraOrganizations) =>
                      existingExtraOrganizations.filter(
                        (existingExtraOrganization) =>
                          existingExtraOrganization.organizationId !==
                          item.organizationId,
                      ),
                    );
                  },
                }}
              >
                {language.delete()}
              </Button>
            );
          },
        },
      ];
    }, [language, organizations.organizations, setExtraOrganizations]);

  const addDisabled = useMemo(() => {
    return (
      !selectedExtraOrganizationId ||
      !selectedExtraOrganizationRole ||
      extraOrganizations.some(
        (extraOrganization) =>
          extraOrganization.organizationId === selectedExtraOrganizationId,
      )
    );
  }, [
    selectedExtraOrganizationId,
    selectedExtraOrganizationRole,
    extraOrganizations,
  ]);

  const addBehavior: TouchTargetBehavior = useMemo(() => {
    if (!selectedExtraOrganizationId || !selectedExtraOrganizationRole) {
      return {
        type: 'onClick',
        onClick: () => {},
      };
    }

    return {
      type: 'onClick',
      onClick: () => {
        setExtraOrganizations((existingExtraOrganizations) => [
          ...existingExtraOrganizations,
          {
            organizationId: selectedExtraOrganizationId,
            organizationRole: selectedExtraOrganizationRole,
          },
        ]);
      },
    };
  }, [
    selectedExtraOrganizationId,
    selectedExtraOrganizationRole,
    setExtraOrganizations,
  ]);

  const organizationList: ListItem<string>[] = useMemo(() => {
    return organizations.organizations.map((organization) => ({
      optionLabel: organization.name,
      optionValue: organization.id,
    }));
  }, [organizations]);

  const roleList: ListItem<OrganizationUserRole>[] = useMemo(() => {
    return mapOverEnum(OrganizationUserRole, (role) => ({
      optionLabel: role,
      optionValue: role,
    }));
  }, []);

  return (
    <Spacing size="md" direction="vertical">
      <S2Typography
        variant={'headline-4'}
        translatedContent={language.extraOrganizations()}
      />
      <S2Typography
        variant={'body-6'}
        translatedContent={language.extraOrganizationsHelp()}
      />
      <Spacing size="md" direction="horizontal">
        <SelectBoxWithSearch
          type="single"
          list={organizationList}
          label={language.organization()}
          setSelected={setSelectedExtraOrganizationId}
          selected={selectedExtraOrganizationId}
        />
        <SelectBoxWithSearch
          type="single"
          list={roleList}
          label={language.role()}
          setSelected={(r) => {
            setSelectedExtraOrganizationRole(r);
          }}
          selected={selectedExtraOrganizationRole ?? 'user'}
        />
        <Button behavior={addBehavior} disabled={addDisabled}>
          {language.addExtraOrganization()}
        </Button>
      </Spacing>
      <Table items={extraOrganizations} columnDefinitions={columnDefinitions} />
    </Spacing>
  );
};
